import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * HeroSection — Fully reusable carousel hero component.
 *
 * Responsive behaviour:
 *   Mobile        → image on top (with arrows overlaid), text below
 *   Tablet/Desktop → image flush-LEFT (absolute), text content on RIGHT, vertically centered
 *
 * Slide object shape:
 * {
 *   id, image, alt,
 *   headingStrong, headingSub, description,
 *   badge,
 *   cta: { primary: { label, href, action? }, secondary: { label, href, action? } }
 * }
 */
const HeroSectionSkeleton = () => (
  <section className="relative w-full bg-[var(--ava-color-primary-50)] overflow-hidden">
    {/* MOBILE */}
    <div className="flex flex-col md:hidden">
      <div className="ava-skeleton w-full" style={{ aspectRatio: '4 / 3' }} />
      <div className="px-4 pt-6 pb-10 flex flex-col items-start gap-3">
        <div className="ava-skeleton" style={{ width: '85%', height: 26, borderRadius: 8 }} />
        <div className="ava-skeleton" style={{ width: '55%', height: 18, borderRadius: 8 }} />
        <div className="ava-skeleton" style={{ width: '95%', height: 14, borderRadius: 6 }} />
        <div className="ava-skeleton" style={{ width: '70%', height: 14, borderRadius: 6 }} />
        <div className="flex gap-3 mt-1">
          <div className="ava-skeleton" style={{ width: 120, height: 42, borderRadius: 10 }} />
          <div className="ava-skeleton" style={{ width: 120, height: 42, borderRadius: 10 }} />
        </div>
        <div className="ava-skeleton w-full" style={{ height: 44, borderRadius: 12, marginTop: 8 }} />
        <div className="flex gap-2 flex-wrap mt-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ava-skeleton" style={{ width: 70 + (i % 3) * 20, height: 24, borderRadius: 20 }} />
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 mt-2 w-full">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="ava-skeleton"
              style={{ width: i === 0 ? 24 : 8, height: 8, borderRadius: 999 }}
            />
          ))}
        </div>
      </div>
    </div>

    {/* TABLET / DESKTOP */}
    <div className="hidden md:flex relative" style={{ minHeight: 'clamp(320px, 37vw, 480px)' }}>
      <div className="ava-skeleton absolute left-0 top-0 h-full w-[45vw]" />
      <div className="relative z-10 ml-[45vw] px-[3vw] lg:px-[4vw] xl:px-[5vw] flex-1 flex items-center">
        <div className="max-w-[520px] flex flex-col items-start gap-3 py-10 w-full">
          <div className="ava-skeleton" style={{ width: '80%', height: 34, borderRadius: 8 }} />
          <div className="ava-skeleton" style={{ width: '50%', height: 22, borderRadius: 8 }} />
          <div className="ava-skeleton" style={{ width: '95%', height: 14, borderRadius: 6 }} />
          <div className="ava-skeleton" style={{ width: '75%', height: 14, borderRadius: 6 }} />
          <div className="flex gap-3 mt-1">
            <div className="ava-skeleton" style={{ width: 140, height: 46, borderRadius: 10 }} />
            <div className="ava-skeleton" style={{ width: 140, height: 46, borderRadius: 10 }} />
          </div>
          <div className="ava-skeleton" style={{ width: '100%', maxWidth: 480, height: 48, borderRadius: 12, marginTop: 8 }} />
          <div className="flex gap-2 flex-wrap mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="ava-skeleton" style={{ width: 80 + (i % 3) * 20, height: 26, borderRadius: 20 }} />
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* DESKTOP dot indicators */}
    <div
      className="hidden md:flex absolute bottom-4 z-20 items-center gap-2"
      style={{ left: '50%', transform: 'translateX(-50%)' }}
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="ava-skeleton"
          style={{ width: i === 0 ? 24 : 8, height: 8, borderRadius: 999 }}
        />
      ))}
    </div>
  </section>
);
const HeroSection = ({ slides = [], autoPlayInterval = 10000,loading = false  }) => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  const goToSlide = useCallback(
    (index) => {
      if (animating || index === current) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 700);
    },
    [animating, current]
  );

  const nextSlide = useCallback(
    () => goToSlide((current + 1) % slides.length),
    [current, slides.length, goToSlide]
  );

  const prevSlide = useCallback(
    () => goToSlide((current - 1 + slides.length) % slides.length),
    [current, slides.length, goToSlide]
  );

  useEffect(() => {
    if (slides.length <= 1 || paused) {
      console.log('[HeroSection] Timer PAUSED ⏸');
      return;
    };
    console.log('[HeroSection] Timer RUNNING ▶');
    const timer = setInterval(nextSlide, autoPlayInterval);
    return () => clearInterval(timer);
  }, [nextSlide, autoPlayInterval, slides.length, paused]);

  // if (!slides.length) return null;

  /* ── Smart CTA link handler ── */
  // const handleCtaClick = (e, cta) => {
  //   const href = cta?.href || cta;
  //   const action = cta?.action || null;

  //   if (action === 'book-appointment') {
  //     e.preventDefault();
  //     navigate('/book-appointment');
  //     return;
  //   }
  //   if (!href || href === '#') { e.preventDefault(); return; }
  //   if (href.startsWith('http://') || href.startsWith('https://')) return;

  //   e.preventDefault();
  //   const [path, hash] = href.split('#');
  //   if (!path) {
  //     document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //     return;
  //   }
  //   navigate(path + (hash ? `#${hash}` : ''), { state: { scrollTo: hash || null } });
  // };

  const handleCtaClick = (e, cta) => {
    const href = cta?.href ?? cta;
    const action = cta?.action || null;

    // force href to string only if valid
    const hrefStr = typeof href === "string" ? href : "";

    if (action === "book-appointment") {
      e.preventDefault();
      navigate("/book-appointment");
      return;
    }

    if (!hrefStr || hrefStr === "#") {
      e.preventDefault();
      return;
    }

    // external links
    if (
      hrefStr.startsWith("http://") ||
      hrefStr.startsWith("https://")
    ) {
      return;
    }

    e.preventDefault();

    const [path, hash] = hrefStr.split("#");

    if (!path) {
      document
        .getElementById(hash)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    navigate(path + (hash ? `#${hash}` : ""), {
      state: { scrollTo: hash || null },
    });
  };
//  if (loading) return <HeroSectionSkeleton />
 if (loading) return <HeroSectionSkeleton />;
  if (!slides.length) return null;
  return (
    <section
      id="hero"
      className="relative w-full bg-[var(--ava-color-primary-50)] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ─────────────────────────────────────────
          SLIDES
      ───────────────────────────────────────── */}
      {slides.map((slide, index) => {
        const isActive = index === current;
        return (
          <div
            key={slide.id}
            className="transition-opacity duration-700 ease-in-out"
            style={{
              opacity: isActive ? 1 : 0,
              position: isActive ? 'relative' : 'absolute',
              inset: isActive ? 'auto' : 0,
              zIndex: isActive ? 1 : 0,
              pointerEvents: isActive ? 'auto' : 'none',
            }}
            aria-hidden={!isActive}
          >

            {/* ══════════════════════════════════════
                MOBILE  (hidden on md+)
                Image on top → text below
            ══════════════════════════════════════ */}
            <div className="flex flex-col md:hidden">

              {/* Image + overlaid arrows */}
              {slide.image && (
                <div className="relative w-full ">
                  <img
                    src={slide.image}
                    alt={slide.alt || ''}
                    className="w-full h-auto object-contain "
                    draggable={false}
                  />

                  {/* Prev arrow */}
                  {slides.length > 1 && (
                    <button
                      onClick={prevSlide}
                      aria-label="Previous slide"
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10
                                 w-8 h-8 flex items-center justify-center
                                 rounded-full bg-white bg-opacity-80
                                 hover:bg-opacity-100 shadow-md
                                 transition-all duration-200 border border-[var(--primary-color)]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="var(--primary-color)" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  {/* Next arrow */}
                  {slides.length > 1 && (
                    <button
                      onClick={nextSlide}
                      aria-label="Next slide"
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10
                                 w-8 h-8 flex items-center justify-center
                                 rounded-full bg-white bg-opacity-80
                                 hover:bg-opacity-100 shadow-md
                                 transition-all duration-200 border border-[var(--primary-color)]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="var(--primary-color)" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              )}

              {/* Text block */}
              <div className="px-4 pt-6 pb-10 flex flex-col items-start gap-3">
                <SlideText
                  slide={slide}
                  isActive={isActive}
                  handleCtaClick={handleCtaClick}
                />

                {/* Mobile dot indicators */}
                {slides.length > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-2 w-full">
                    {/* // <div className="flex items-center gap-2 mt-2"> */}
                    {slides.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => goToSlide(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        className="transition-all duration-300 rounded-full"
                        style={{
                          width: i === current ? '24px' : '8px',
                          height: '8px',
                          backgroundColor:
                            i === current ? 'var(--primary-color)' : 'rgba(0,0,0,0.2)',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ══════════════════════════════════════
                TABLET / DESKTOP  (hidden below md)
                Image flush-LEFT top-to-bottom (absolute)
                Text content on RIGHT, vertically centered
            ══════════════════════════════════════ */}
            <div
              className="hidden md:flex relative"
              style={{ minHeight: 'clamp(320px, 37vw, 480px)' }}
            >
              {/* LEFT: image — absolutely flush to left edge, full height, no gaps */}
              {slide.image && (
                <div className="absolute left-0 top-0 h-full w-[45vw]">
                  <img
                    src={slide.image}
                    alt={slide.alt || ''}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
              )}

              {/* RIGHT: flex-1 fills remaining width, items-center vertically centers */}
              <div className="relative z-10 ml-[45vw] px-[3vw] lg:px-[4vw] xl:px-[5vw] flex-1 flex items-center">
                <div className="max-w-[520px] flex flex-col items-start gap-3 py-10">
                  <SlideText
                    slide={slide}
                    isActive={isActive}
                    handleCtaClick={handleCtaClick}
                  />
                </div>
              </div>
            </div>

          </div>
        );
      })}

      {/* ─────────────────────────────────────────
          DESKTOP — Prev & Next Arrows
      ───────────────────────────────────────── */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            aria-label="Previous slide"
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-20
                       w-9 h-9 items-center justify-center rounded-full
                       bg-white bg-opacity-80 hover:bg-opacity-100 shadow-md
                       transition-all duration-200 border border-[var(--primary-color)]"
          >
            <svg className="w-4 h-4" fill="none" stroke="var(--primary-color)" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            aria-label="Next slide"
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-20
                       w-9 h-9 items-center justify-center rounded-full
                       bg-white bg-opacity-80 hover:bg-opacity-100 shadow-md
                       transition-all duration-200 border border-[var(--primary-color)]"
          >
            <svg className="w-4 h-4" fill="none" stroke="var(--primary-color)" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* ─────────────────────────────────────────
          DESKTOP — Dot Indicators
      ───────────────────────────────────────── */}
      {slides.length > 1 && (
        <div
          className="hidden md:flex absolute bottom-4 z-20 items-center gap-2"
          style={{ left: '50%', transform: 'translateX(-50%)' }}
        >
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              className="transition-all duration-300 rounded-full"
              style={{
                width: index === current ? '24px' : '8px',
                height: '8px',
                backgroundColor:
                  index === current ? 'var(--primary-color)' : 'rgba(0,0,0,0.2)',
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ─────────────────────────────────────────────
   SlideText — shared text block (mobile + desktop)
───────────────────────────────────────────── */
const SlideText = ({ slide, isActive, handleCtaClick }) => {
const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const chips = slide.chips ?? [
    'Irregular Periods', 'PCOS', 'Fertility', 'Pregnancy Care', 'Menopause'
  ];
  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <>
      {/* Heading */}
      {(slide.headingStrong || slide.headingSub) && (
        <h1 className="ava-heading-xl">
          {slide.headingStrong && (
            <span className="block">{slide.headingStrong}</span>
          )}
          {slide.headingSub && (
            <span className="ava-heading-md">
              {slide.headingSub}
            </span>
          )}
        </h1>
      )}

      {/* Description */}
      {slide.description && (
        <p
          className="ava-text-body"
          style={{ animation: isActive ? 'heroFadeUp 0.7s 0.15s ease both' : 'none' }}
        >
          {slide.description}
        </p>
      )}

      {/* CTA Buttons */}
      {slide.cta && (
        <div
          className="flex flex-wrap items-center gap-3"
          style={{ animation: isActive ? 'heroFadeUp 0.7s 0.3s ease both' : 'none' }}
        >
          {slide.cta.secondary && (
            <a
              href={slide.cta.secondary.href || '#'}
              className="btn-transparent"
              style={{ borderColor: 'var(--primary-color)' }}
              onClick={(e) => handleCtaClick(e, slide.cta.secondary)}
            >
              {slide.cta.secondary.label}
            </a>
          )}
          {slide.cta.primary && (
            <a
              href={slide.cta.primary.href || '#'}
              className="btn-primary"
              onClick={(e) => handleCtaClick(e, slide.cta.primary)}
            >
              {slide.cta.primary.label}
            </a>
          )}
        </div>
      )}

      {/* ── Search bar ── */}
      {slide.showSearch !== false && (
        <form
          onSubmit={handleSearch}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-white"
          style={{
            border: '1.5px solid var(--gray-200, #e5e7eb)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
            maxWidth: 480,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search for Doctors, Services, Programs..."
            className="flex-1 outline-none bg-transparent text-sm"
            style={{ color: 'var(--text-dark)', minWidth: 0 }}
          />
        </form>
      )}

      {/* ── Chips ── */}
      {slide.showSearch !== false && chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <button
              key={chip}
              onClick={() => navigate(`/search?q=${encodeURIComponent(chip)}`)}
              className="px-3 py-1 rounded-full text-xs font-medium bg-white transition-colors hover:bg-gray-50"
              style={{
                border: '1px solid var(--gray-200, #e5e7eb)',
                color: 'var(--text-dark)',
                cursor: 'pointer',
              }}
            >
              {chip}
            </button>
          ))}
        </div>
      )}


      {/* Badge */}
      {slide.badge && (
        <div
          className="flex items-center gap-2 text-[0.82rem] sm:text-[0.85rem]"
          style={{ animation: isActive ? 'heroFadeUp 0.7s 0.45s ease both' : 'none' }}
        >
          <svg
            className="w-[18px] h-[18px] flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
          <span>{slide.badge}</span>
        </div>
      )}
    </>
  )
}

export default HeroSection;